import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PageTitle from "../../../components/common/PageTitle/PageTitle";
import { CircularIndeterminate } from "../../../components/common/CircularIndeterminate";
import { MainContentWrapper } from "../../../components/common/Common.styles";
import { GeneralButton } from "../../../common/ui/GeneralButton";
import { useAppDispatch } from "../../../hooks/StoreHooks";
import { useProcessingRequest } from "../../../store/selectors/tableManagementSelector";
import { useWalletTypes } from "../../../store/selectors/walletTypesSelector";
import {
  createWalletTypeThunk,
  deleteWalletTypeThunk,
  fetchWalletTypesThunk,
  updateWalletTypeThunk,
} from "../../../store/thunk/walletTypesThunk";
import { WalletType } from "../../../types/walletType";
import WalletTypesTable from "./WalletTypesTable";
import EditWalletTypeDialog, { WalletTypeFormData } from "./EditWalletTypeDialog";

export default function WalletTypesPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const processingRequest = useProcessingRequest();
  const walletTypes = useWalletTypes();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingWalletType, setEditingWalletType] = useState<WalletType | undefined>();

  useEffect(() => {
    dispatch(fetchWalletTypesThunk());
  }, [dispatch]);

  const handleSave = (data: WalletTypeFormData, id: string | undefined) => {
    if (id) {
      dispatch(
        updateWalletTypeThunk({
          id,
          walletType: {
            name: data.name,
            description: data.description,
            isFallback: data.isFallback,
          },
        })
      );
    } else {
      dispatch(
        createWalletTypeThunk({
          name: data.name,
          description: data.description,
        })
      );
    }
  };

  const handleDelete = (walletType: WalletType) => {
    dispatch(deleteWalletTypeThunk(walletType.id));
  };

  return (
    <>
      <PageTitle breadcrumbs={[]} title={t("walletTypes.title")} />

      <MainContentWrapper>
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2 }}>
          <GeneralButton
            appVariant="primary"
            onClick={() => setIsCreateDialogOpen(true)}
          >
            <AddIcon sx={{ mr: 1 }} />
            {t("walletTypes.createButton")}
          </GeneralButton>
        </Box>

        <WalletTypesTable
          walletTypes={walletTypes}
          handleEditWalletType={(walletType) => setEditingWalletType(walletType)}
          handleDeleteWalletType={handleDelete}
        />
      </MainContentWrapper>

      {isCreateDialogOpen && (
        <EditWalletTypeDialog
          onClose={() => setIsCreateDialogOpen(false)}
          onSave={handleSave}
        />
      )}
      {editingWalletType && (
        <EditWalletTypeDialog
          onClose={() => setEditingWalletType(undefined)}
          onSave={handleSave}
          walletType={editingWalletType}
        />
      )}

      <CircularIndeterminate processingRequest={processingRequest} />
    </>
  );
}
